<template>
    <!-- ACTION BAR -->
    <div class="container-action-bar">
        <CompassTooltip :arrow="false">
            <template #content>
                <span>{{ labels.showHistoryTooltip }}</span>
            </template>
            <span>
                <CompassIconButton
                    icon="history"
                    size="small"
                    :active="displayHistory"
                    @click="$emit('toggleHistory')"
                />
            </span>
        </CompassTooltip>
        <CompassTooltip :arrow="false">
            <template #content>
                <span>{{ pinStatusLabel }}</span>
            </template>
            <span>
                <CompassIconButton
                    icon="pin"
                    size="small"
                    :active="isPinned"
                    @click.stop="onClickPin"
                />
            </span>
        </CompassTooltip>
        <CompassTooltip v-if="oneLog.canSee" :arrow="false">
            <template #content>
                <span>{{ labels.seeDetailsTooltip }}</span>
            </template>
            <span
                ><CompassIconButton
                    icon="panel-right-expand"
                    size="small"
                    @click="previewElement(oneLog)"
            /></span>
        </CompassTooltip>
        <!-- Custom tooltip for 'more actions' -->
        <div ref="customTooltip" :class="customTooltipClasses">
            {{ labels.moreActionsTooltip }}
        </div>
        <span v-if="canEdit || canDelete">
            <CompassMenu
                ref="actionBarMenu"
                size="small"
                placement="top-end"
                customClass="container-action-bar__menu"
                :disabled="!isActionBarVisible"
            >
                <CompassIconButton
                    icon="action"
                    size="small"
                    @click="handleCustomTooltipVisibility"
                    @mouseenter="handleCustomTooltipVisibility"
                    @mouseleave="handleCustomTooltipVisibility"
                />
                <template #dropdown>
                    <CompassDropdownOption
                        v-if="canEdit"
                        :label="labels.menuItemModify"
                        @itemSelected="editElement(oneLog)"
                    />
                    <CompassDropdownOption
                        v-if="canDelete"
                        :label="labels.menuItemDelete"
                        @itemSelected="deleteElement(oneLog)"
                    />
                </template>
            </CompassMenu>
        </span>
    </div>
</template>
<script>
import { createNamespacedHelpers } from 'vuex';
import { debounce } from 'lodash';
import Feature from 'Services/Feature';

const { mapActions: mapActionsPopup } = createNamespacedHelpers('popupPanel');
const { mapState: mapStateUser } = createNamespacedHelpers('user');
export default {
    name: 'TimelineActionBar',
    props: {
        oneLog: {
            type: Object,
            required: true,
        },
        isActionBarVisible: {
            type: Boolean,
            required: true,
        },
        isPinned: {
            type: Boolean,
            required: true,
        },
        pinStatusLabel: {
            type: String,
            required: true,
        },
        displayHistory: {
            type: Boolean,
            required: true,
        },
        linkedid: {
            type: Number,
            required: true,
        },
        linkedtype: {
            type: String,
            required: true,
        },
        subtype: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            labels: {
                showHistoryTooltip: this.$l10n.__("Afficher l'historique"),
                seeDetailsTooltip: this.$l10n.__('Voir le détail'),
                moreActionsTooltip: this.$l10n.__("Plus d'actions"),
                menuItemModify: this.$l10n.__('Modifier'),
                menuItemDelete: this.$l10n.__('Supprimer'),
            },
            isTooltipVisible: false,
        };
    },
    computed: {
        ...mapStateUser({
            timezone: 'timezone',
            userForename: 'forename',
            userName: 'name',
        }),
        canDelete() {
            return this.oneLog.can_delete;
        },
        canEdit() {
            return this.oneLog.can_edit;
        },
        customTooltipClasses() {
            return {
                'container-action-bar__custom-tooltip-more': this.isTooltipVisible,
                'typographies-paragraph-small-bold': this.isTooltipVisible,
                'container-action-bar__custom-tooltip-more-hidden': !this.isTooltipVisible,
            };
        },
        // eslint-disable-next-line vue/no-unused-properties
        isNewPanelEnabled() {
            return Feature.isEnabled('activity-new-creation-panel');
        },
    },
    methods: {
        ...mapActionsPopup(['open']),
        // eslint-disable-next-line vue/no-unused-properties
        openDetail(type, callback, callbackOnDelete, callbackOnCreate, related) {
            if (['delivery', 'proforma'].includes(this.oneLog.relatedtype)) {
                Activities.openOldTaskDetail(
                    this.oneLog.objectid,
                    this.oneLog.can_edit,
                    this.oneLog.timestamp,
                    this.oneLog.relatedtype,
                );
            } else {
                this.open({
                    key: 'activityDetailPanel',
                    props: {
                        activityDetailDisplayed: {
                            id: this.oneLog.objectid,
                            type: type,
                            related: related ?? [
                                {
                                    id: +(this.oneLog.relatedid ?? 0),
                                    type: this.oneLog.relatedtype,
                                    subtype: this.subtype,
                                },
                            ],
                        },
                        callback,
                        callbackOnDelete,
                        callbackOnCreate,
                    },
                });
            }
        },
        previewElement(onelog) {
            //Method implemented in child components
        },
        editElement(onelog) {
            //Method implemented in child components
        },
        deleteElement(onelog) {
            //Method implemented in child components
        },
        handleCustomTooltipVisibility(event) {
            setTimeout(() => {
                this.isTooltipVisible = false;
                if (
                    !this.$refs.actionBarMenu?.isVisible &&
                    ['mouseenter', 'click'].includes(event.type)
                ) {
                    //The action bar menu is not visible so show the tooltip
                    this.isTooltipVisible = true;
                }
            }, 0);
        },
        onClickPin: debounce(function () {
            this.$emit('toggle-pin');
        }, 1000),
    },
};
</script>

<style lang="scss">
.container-action-bar {
    display: inline-flex;
    padding: var(--spacing-0);
    align-items: flex-start;
    gap: var(--spacing-1);
    position: absolute;
    right: var(--spacing-1);
    top: -16px;
    border-radius: var(--spacing-0);
    border: 1px solid var(--colors-common-border-lighter);
    background: var(--colors-common-background-default);

    &__custom-tooltip-more {
        overflow: hidden;
        position: absolute;
        top: 32px;
        right: -33px;
        display: flex;
        padding: var(--spacing-0) var(--spacing-1);
        justify-content: center;
        align-items: center;
        border-radius: var(--spacing-0);
        background: var(--colors-common-background-default-contrast);
        color: var(--colors-common-text-standard-on-color);
    }

    &__custom-tooltip-more-hidden {
        display: none;
    }
}
</style>

<style lang="scss">
#compass-menu-container {
    .container-action-bar__menu {
        z-index: 100;
    }
}
</style>
